<script lang="ts" setup>
import Logo from "@/assets/Logos/Logo.svg";
import Button from "@/components/Reusables/Buttons/Button.vue";
import LoadingLogoSpinner from "@/components/Reusables/Loading/LoadingLogoSpinner.vue";
import useHasuraStore from "@/stores/hasura/hasura.store";
import { storeToRefs } from "pinia";
import { ref, watch } from "vue";

const hasuraStore = useHasuraStore();
const { status } = storeToRefs(hasuraStore);

const RETRY_SECONDS = 5;
const countdown = ref(RETRY_SECONDS);
const isRetrying = ref(false);
let countdownInterval: ReturnType<typeof setInterval> | null = null;
let retryDisplayTimeout: ReturnType<typeof setTimeout> | null = null;

const stopCountdown = () => {
  if (countdownInterval !== null) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
};

const clearRetryDisplay = () => {
  if (retryDisplayTimeout !== null) {
    clearTimeout(retryDisplayTimeout);
    retryDisplayTimeout = null;
  }
};

const startCountdown = () => {
  stopCountdown();
  clearRetryDisplay();
  countdown.value = RETRY_SECONDS;
  countdownInterval = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      stopCountdown();
      triggerRetry();
    }
  }, 1000);
};

// Show "Retrying..." for 5 seconds regardless of how fast the request completes,
// then return to countdown if still failing.
const triggerRetry = () => {
  stopCountdown();
  clearRetryDisplay();
  isRetrying.value = true;
  hasuraStore.retry();
  retryDisplayTimeout = setTimeout(() => {
    retryDisplayTimeout = null;
    isRetrying.value = false;
    if (status.value === "error") {
      startCountdown();
    }
  }, RETRY_SECONDS * 1000);
};

const handleRetry = () => {
  triggerRetry();
};

watch(
  status,
  (newStatus) => {
    if (newStatus === "error" && !isRetrying.value) {
      // Only start countdown if not already in a retry display cycle
      startCountdown();
    } else if (newStatus === "authenticated") {
      isRetrying.value = false;
      stopCountdown();
      clearRetryDisplay();
    }
    // During loading (retry in flight): keep current state
  },
  { immediate: true }
);
</script>

<template>
  <!-- Loading (initial connection only) -->
  <template v-if="['unauthenticated', 'loading'].includes(status) && !isRetrying">
    <div class="absolute flex h-screen w-full flex-col items-center justify-center">
      <LoadingLogoSpinner />
      <span class="mt-4">Connecting...</span>
    </div>
  </template>

  <!-- No hasura token -->
  <template v-if="status === 'error' || isRetrying">
    <div class="flex h-screen flex-col items-center justify-center space-y-2">
      <img
        :src="Logo"
        alt="Real-Time LCA" />
      <h4 class="text-2xl">Dang-it! Something went wrong...</h4>
      <p>The connection to the database was interrupted.</p>
      <p>
        Please contact
        <a
          class="text-app-blue-600 hover:text-app-blue-700"
          href="mailto:support@realtimelca.com">
          support@realtimelca.com
        </a>
        if the problem persists.
      </p>
      <div class="flex flex-col items-center space-y-2 pt-2">
        <Button
          class="min-w-[9rem]"
          color="primary"
          :label="isRetrying ? 'Retrying…' : 'Try again'"
          :is-loading="isRetrying"
          @click="handleRetry" />
        <span
          v-if="!isRetrying"
          class="text-type-muted text-sm"
          >Retrying automatically in {{ countdown }} second{{ countdown !== 1 ? "s" : "" }}...</span
        >
      </div>
    </div>
  </template>
  <template v-if="status === 'authenticated'">
    <slot />
  </template>
</template>
